import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Users, Pencil, Eye, EyeOff, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DataOrangTua() {
  const [parents, setParents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [shown, setShown] = useState({});
  const [query, setQuery] = useState("");

  const load = () => api.get("/admin/parents").then((r) => setParents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name: "", phone: "", password: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, phone: p.phone, password: "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/parents/${editing.id}`, form);
        toast.success("Data orang tua diperbarui");
      } else {
        await api.post("/admin/parents", form);
        toast.success("Akun orang tua dibuat");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = parents.filter((p) => {
    const q = query.toLowerCase();
    return p.name.toLowerCase().includes(q) || (p.phone || "").includes(query);
  });
  const pg = usePaged(filtered, 10);

  const del = async (p) => {
    if (!window.confirm(`Hapus akun orang tua "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/parents/${p.id}`);
      toast.success(`Akun "${p.name}" dihapus`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Data Orang Tua"
        subtitle="Akun login berdasarkan Nomor HP. Admin dapat melihat & mengatur ulang password."
        action={
          <Button onClick={openAdd} data-testid="add-parent-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
            <UserPlus size={18} className="mr-2" /> Tambah Orang Tua
          </Button>
        }
      />

      <div className="relative mb-4 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="parent-search" placeholder="Cari nama / nomor HP…" className="pl-10 rounded-xl py-6 bg-white" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {parents.length === 0 ? (
          <Empty><Users className="mx-auto mb-2" /> Belum ada akun orang tua.</Empty>
        ) : filtered.length === 0 ? (
          <Empty><Search className="mx-auto mb-2" /> Tidak ada hasil untuk "{query}".</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nama</TableHead>
                <TableHead>Nomor HP</TableHead>
                <TableHead>Password</TableHead>
                <TableHead className="text-center">Anak</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((p) => (
                <TableRow key={p.id} data-testid={`parent-row-${p.id}`}>
                  <TableCell className="font-medium text-stone-800">{p.name}</TableCell>
                  <TableCell className="font-mono text-sm">{p.phone}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm" data-testid={`parent-pw-${p.id}`}>
                        {shown[p.id] ? p.password_plain : "••••••••"}
                      </span>
                      <button onClick={() => setShown((s) => ({ ...s, [p.id]: !s[p.id] }))} data-testid={`toggle-pw-${p.id}`} className="text-stone-400 hover:text-emerald-600">
                        {shown[p.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">{p.children}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-parent-${p.id}`} className="rounded-lg">
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(p)} data-testid={`del-parent-${p.id}`} className="ml-1 text-rose-600">
                      <Trash2 size={15} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Orang Tua" : "Tambah Orang Tua"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Nama Lengkap</Label>
              <Input data-testid="parent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Nomor HP</Label>
              <Input data-testid="parent-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="rounded-xl" placeholder="08xxxx" />
            </div>
            <div className="space-y-2">
              <Label>{editing ? "Password Baru (kosongkan jika tidak diubah)" : "Password"}</Label>
              <Input data-testid="parent-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} className="rounded-xl" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-parent-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                {saving ? "Menyimpan…" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
